import { Text } from '@shared/components';
import { ActionTimelineEvent } from '@shared/components/events-timeline/components/action-timeline-event';
import { InlineTextArea } from '@shared/components/inline-text-area/InlineTextArea';
import { InlineTextField } from '@shared/components/inline-text-field/InlineTextField';
import { useToast } from '@shared/components/toast';
import {
  TriggerSopAppointmentSchedulingMutation,
  UpdatedPlace,
  useTriggerSopAppointmentSchedulingMutation as useScheduleEmail,
} from '@shared/generated/graphql';
import { parseTime } from 'clerk_common/features/parseTime';
import { ScheduleStop } from 'clerk_common/types';
import moment from 'moment-timezone';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ApptStatusChip } from './ApptStatusChip';
import { ScheduleButton, StopProps } from './Stop';
import {
  createDateTime,
  formatParsedTime,
  formatTime,
  knownAndInProgress,
  notYetScheduledOrUnknown,
} from './util';

type StopForm = {
  emailsUnparsed: string;
  time: string;
};

export function EmailStop({
  stop,
  index,
  shipmentId,
  refetchShipment,
}: StopProps) {
  const [submitEnabled, setSubmitEnabled] = useState<boolean>(true);
  const options = { onCompleted: onScheduleCompleted };
  const [triggerScheduling] = useScheduleEmail(options);
  const { sendToast } = useToast();
  const defaultValues: StopForm = getDefaultValues(stop);
  const methods = useForm<StopForm>({ defaultValues });

  async function scheduleStop() {
    const time = parseTime(methods.getValues('time'));
    const requestedDt = toRequestedDt(stop, time);
    if (methods.getValues('emailsUnparsed').length == 0 || time === undefined) {
      sendToast('Please enter an email and time.');
      return;
    }

    const emailsUnparsed = methods.getValues('emailsUnparsed');
    const placesToUpdate = toPlacesToUpdate(stop.placeId, emailsUnparsed);
    const form = { emailsUnparsed, requestedDt };
    const input = {
      shipmentId,
      stopsIndex: index,
      placesToUpdate,
      form,
      movementStopId: stop.movementStopId,
    };
    setSubmitEnabled(false);
    await triggerScheduling({ variables: { input } });
  }

  async function onScheduleCompleted(
    data: TriggerSopAppointmentSchedulingMutation
  ) {
    const { success, error } = data.triggerSOPAppointmentScheduling;
    if (!success && error) {
      sendToast(error);
    }
    if (success) {
      await refetchShipment();
    }
    setSubmitEnabled(true);
  }

  const status = stop.appointmentStatus;
  const isNotYetScheduled = notYetScheduledOrUnknown(status);
  const isInProgress = knownAndInProgress(status);

  function formatTime() {
    if (methods.getValues('time').length == 0) return;
    const unparsedTime = methods.getValues('time');
    const parsedTime = parseTime(unparsedTime);
    if (parsedTime) {
      methods.setValue('time', formatParsedTime(parsedTime));
    } else {
      methods.setValue('time', '');
    }
  }

  return (
    <FormProvider {...methods}>
      <ActionTimelineEvent type={stop.stopType}>
        <div className="mb-4 ml-2 mt-1 text-gray-700">
          <Text isHeavy type="body-xs">
            {stop.locationLabel}
          </Text>
          <Text type="body-xs">{stop.address.addressOne}</Text>
          <Text type="body-xs">{humanizeDate(stop.day)}</Text>
          {isNotYetScheduled && (
            <>
              <div className="my-3 rounded-md border bg-gray-50 p-2">
                <InlineTimeField formatTime={formatTime} />
                <InlineEmailsField emails={stop.stopEmails} />
              </div>
              <div>
                <ScheduleButton
                  label="Request Appointment"
                  onPress={scheduleStop}
                  disabled={!submitEnabled}
                />
              </div>
            </>
          )}
          {isInProgress && <ApptStatusChip stop={stop} />}
        </div>
      </ActionTimelineEvent>
    </FormProvider>
  );
}

function InlineTimeField({ formatTime }: { formatTime: () => void }) {
  return (
    <InlineTextField
      name="time"
      label="Time"
      type="text"
      save={formatTime}
      required={false}
    />
  );
}

function InlineEmailsField({ emails }: { emails: string[] }) {
  const isMultipleEmails = emails.length > 1;
  const name = 'emailsUnparsed';
  const label = 'Emails';

  if (isMultipleEmails) {
    return (
      <InlineTextArea
        name={name}
        label={label}
        save={() => {}}
        required={false}
        className="mt-3"
      />
    );
  } else {
    return (
      <InlineTextField
        name={name}
        label={label}
        save={() => {}}
        required={false}
      />
    );
  }
}

function toPlacesToUpdate(
  placeId: string | undefined | null,
  emailsUnparsed?: string
): UpdatedPlace[] {
  if (!placeId) return [];
  const place: UpdatedPlace = {
    id: placeId,
    emailsUnparsed: emailsUnparsed ?? '',
  };
  return [place];
}

function getDefaultValues(stop: ScheduleStop): StopForm {
  const values: StopForm = {
    emailsUnparsed: stop.stopEmails.join(', '),
    time: stop.suggestedDt
      ? formatTime(stop.suggestedDt, stop.placeTimeZone)
      : '',
  };
  return values;
}

function toRequestedDt(
  stop: ScheduleStop,
  time: { hours: number; minutes: number } | undefined
): Date | undefined {
  const day = stop.day;
  if (!day || !time) return;
  const timezone = stop.placeTimeZone;
  return createDateTime(day, time, timezone);
}

function humanizeDate(day?: string): string {
  if (!day) return '';
  return moment(day).format('dddd, MMM D');
}
